const VELOCITY_SCALE = 0.005 * (Math.pow(window.devicePixelRatio, 1 / 3) || 1)
const MIN_TEMPERATURE_K = 261.15
const MAX_TEMPERATURE_K = 317.15
const MAX_PARTICLE_AGE = 90
const PARTICLE_LINE_WIDTH = 1
const PARTICLE_MULTIPLIER = 1 / 200
const PARTICLE_REDUCTION = Math.pow(window.devicePixelRatio, 1 / 3) || 1.6
const FRAME_RATE = 15
const FRAME_TIME = 1000 / FRAME_RATE
const NULL_WIND_VECTOR = [NaN, NaN, null]

const isValue = x => x !== null && x !== undefined

const floorMod = (a, n) => a - n * Math.floor(a / n)

const mercY = lat => Math.log(Math.tan(lat / 2 + Math.PI / 4))

const deg2rad = deg => deg / 180 * Math.PI

const rad2deg = ang => ang / (Math.PI / 180.0)

// const clamp = (x, range) => Math.max(range[0], Math.min(x, range[1]))

const isMobile = () => /android|blackberry|iemobile|ipad|iphone|ipod|opera mini|webos/i.test(navigator.userAgent)

const bilinearInterpolateVector = (x, y, g00, g10, g01, g11) => {
  let rx = 1 - x
  let ry = 1 - y
  let a = rx * ry
  let b = x * ry
  let c = rx * y
  let d = x * y
  let u = g00[0] * a + g10[0] * b + g01[0] * c + g11[0] * d
  let v = g00[1] * a + g10[1] * b + g01[1] * c + g11[1] * d
  let tmp = g00[2] * a + g10[2] * b + g01[2] * c + g11[2] * d
  return [u, v, tmp]
}

const createWindBuilder = (uComp, vComp) => {
  let uData = uComp.data
  let vData = vComp.data

  return {
    header: uComp.header,
    data: i => [uData[i], vData[i]],
    interpolate: bilinearInterpolateVector
  }
}

const createBuilder = data => {
  let uComp = null
  let vComp = null
  // let scalar = null

  data.forEach(record => {
    let {header} = record

    switch (`${header.parameterCategory},${header.parameterNumber}`) {
      case '2,2':
        uComp = record
        break
      case '2,3':
        vComp = record
        break
      // default:
      //   scalar = record
    }
  })

  return createWindBuilder(uComp, vComp)
}

const project = (lat, lon, windy) => {
  // both in radians, use deg2rad if neccessary
  let yMin = mercY(windy.south)
  let yMax = mercY(windy.north)
  let xFactor = windy.width / (windy.east - windy.west)
  let yFactor = windy.height / (yMax - yMin)

  let y = mercY(deg2rad(lat))
  let x = (deg2rad(lon) - windy.west) * xFactor
  return [x, (yMax - y) * yFactor]
}

const distortion = (projection, λ, φ, x, y, windy) => {
  let τ = 2 * Math.PI
  let H = Math.pow(10, -5.2)
  let hλ = λ < 0 ? H : -H
  let hφ = φ < 0 ? H : -H

  let pλ = project(φ, λ + hλ, windy)
  let pφ = project(φ + hφ, λ, windy)

  // Meridian scale factor (see Snyder, equation 4-3), where R = 1. This handles issue where length of 1º λ
  // changes depending on φ. Without this, there is a pinching effect at the poles.
  let k = Math.cos(φ / 360 * τ)
  return [(pλ[0] - x) / hλ / k, (pλ[1] - y) / hλ / k, (pφ[0] - x) / hφ, (pφ[1] - y) / hφ]
}

const distort = (projection, λ, φ, x, y, scale, wind, windy) => {
  let u = wind[0] * scale
  let v = wind[1] * scale
  let d = distortion(projection, λ, φ, x, y, windy)

  // Scale distortion vectors by u and v, then add.
  wind[0] = d[0] * u + d[2] * v
  wind[1] = d[1] * u + d[3] * v
  return wind
}

const buildBounds = (bounds, width, height) => {
  let upperLeft = bounds[0]
  let lowerRight = bounds[1]
  let x = Math.round(upperLeft[0])
  let y = Math.max(Math.floor(upperLeft[1], 0), 0)
  // let xMax = Math.min(Math.ceil(lowerRight[0], width), width - 1)
  let yMax = Math.min(Math.ceil(lowerRight[1], height), height - 1)
  return {
    x,
    y,
    xMax: width,
    yMax: yMax,
    width,
    height
  }
}

const invert = (x, y, windy) => {
  let mapLonDelta = windy.east - windy.west
  let worldMapRadius = windy.width / rad2deg(mapLonDelta) * 360 / (2 * Math.PI)
  let mapOffsetY = worldMapRadius / 2 * Math.log((1 + Math.sin(windy.south)) / (1 - Math.sin(windy.south)))
  let equatorY = windy.height + mapOffsetY
  let a = (equatorY - y) / worldMapRadius

  let lat = 180 / Math.PI * (2 * Math.atan(Math.exp(a)) - Math.PI / 2)
  let lon = rad2deg(windy.west) + x / windy.width * rad2deg(mapLonDelta)
  return [lon, lat]
}

const createField = (columns, bounds, callback) => {
  const field = (x, y) => {
    if (!columns) {
      return [NaN, NaN, null]
    }

    let column = columns[Math.round(x)]

    return column ? column[Math.min(Math.max(Math.round(y), 0), column.length - 1)] : NULL_WIND_VECTOR
  }

  field.release = () => {
    columns = []
  }

  field.randomize = o => {
    let x, y
    let safetyNet = 0

    do {
      x = Math.round(Math.floor(Math.random() * bounds.width) + bounds.x)
      y = Math.round(Math.floor(Math.random() * bounds.height) + bounds.y)
    } while (field(x, y)[2] === null && safetyNet++ < 30)

    o.x = x
    o.y = y

    return o
  }

  callback(bounds, field)
}

const interpolateField = (grid, bounds, extent, callback) => {
  let projection = {}
  let mapArea = (extent.south - extent.north) * (extent.west - extent.east)
  let velocityScale = VELOCITY_SCALE * Math.pow(mapArea, 0.3)
  let columns = []
  let x = bounds.x

  const interpolateColumn = x => {
    let column = []

    for (let y = bounds.y; y <= bounds.yMax; y += 2) {
      let coord = invert(x, y, extent)

      if (coord) {
        let λ = coord[0]
        let φ = coord[1]

        if (isFinite(λ)) {
          let wind = grid.interpolate(λ, φ)

          if (wind) {
            wind = distort(projection, λ, φ, x, y, velocityScale, wind, extent)
            column[y + 1] = column[y] = wind
          }
        }
      }
    }

    columns[x + 1] = columns[x] = column
  }

  for (; x < bounds.width; x += 2) {
    interpolateColumn(x)
  }

  createField(columns, bounds, callback)
}

export default param => {
  let builder
  let grid
  let date
  let λ0, φ0, Δλ, Δφ, ni, nj

  const buildGrid = (data, callback) => {
    builder = createBuilder(data)

    let {header} = builder

    λ0 = header.lo1
    φ0 = header.la1

    Δλ = header.dx
    Δφ = header.dy

    ni = header.nx
    nj = header.ny

    date = new Date(header.refTime)
    date.setHours(date.getHours() + header.forecastTime)

    grid = []
    let p = 0
    let isContinuous = Math.floor(ni * Δλ) >= 360

    for (let j = 0; j < nj; j++) {
      let row = []

      for (let i = 0; i < ni; i++, p++) {
        row[i] = builder.data(p)
      }

      if (isContinuous) {
        row.push(row[0])
      }

      grid[j] = row
    }

    // eslint-disable-next-line standard/no-callback-literal
    callback({
      date: date,
      interpolate: interpolate
    })
  }

  const interpolate = (λ, φ) => {
    if (!grid) {
      return null
    }

    let i = floorMod(λ - λ0, 360) / Δλ
    let j = (φ0 - φ) / Δφ

    let fi = Math.floor(i)
    let ci = fi + 1
    let fj = Math.floor(j)
    let cj = fj + 1

    let row
    if ((row = grid[fj])) {
      let g00 = row[fi]
      let g10 = row[ci]

      if (isValue(g00) && isValue(g10) && (row = grid[cj])) {
        let g01 = row[fi]
        let g11 = row[ci]

        if (isValue(g01) && isValue(g11)) {
          return builder.interpolate(i - fi, j - fj, g00, g10, g01, g11)
        }
      }
    }

    return null
  }

  let particles, animationLoop

  const animate = (bounds, field, extent) => {
    // const asColorStyle = (r, g, b, a) => `rgba(243,243,238,${a})`

    // const cutHex = h => h.charAt(0) === '#' ? h.substring(1, 7) : h

    // const hexToR = h => parseInt(cutHex(h).substring(2, 4), 16)

    // const hexToB = h => parseInt(cutHex(h).substring(4, 6), 16)

    const windTemperatureColorScale = (minTemp, maxTemp) => {
      // let result = ['#aaa']
      // let result = ['#3c0', '#ffffff']
      // let result = ['#690', '#ffffff']
      let result = ['#ffffff', '#ffffff']
      result.indexFor = m => param.isSatelliteMode | 0
      return result
    }

    let colorStyles = windTemperatureColorScale(MIN_TEMPERATURE_K, MAX_TEMPERATURE_K)
    let buckets = colorStyles.map(() => [])
    let mapArea = (extent.south - extent.north) * (extent.west - extent.east)
    let particleCount = Math.round(bounds.width * bounds.height * PARTICLE_MULTIPLIER * Math.pow(mapArea, 0.24))

    if (isMobile()) {
      particleCount /= PARTICLE_REDUCTION
    }

    particles = particles || []

    if (particles.length > particleCount) {
      particles = particles.slice(0, particleCount)
    }

    for (let i = particles.length; i < particleCount; i++) {
      particles.push(field.randomize({
        age: ~~(Math.random() * MAX_PARTICLE_AGE) + 0
      }))
    }

    const evolve = () => {
      buckets.forEach(bucket => {
        bucket.length = 0
      })

      particles.forEach(particle => {
        if (particle.age > MAX_PARTICLE_AGE) {
          field.randomize(particle).age = ~~(Math.random() * MAX_PARTICLE_AGE / 2)
        }

        let {x, y} = particle
        let v = field(x, y) // vector at current position
        let m = v[2]

        if (m === null) {
          particle.age = MAX_PARTICLE_AGE // particle has escaped the grid, never to return...
        } else {
          let xt = x + v[0]
          let yt = y + v[1]

          if (field(xt, yt)[0] !== null) {
            // Path from (x,y) to (xt,yt) is visible, so add this particle to the appropriate draw bucket.
            particle.xt = xt
            particle.yt = yt
            buckets[colorStyles.indexFor(m)].push(particle)
          } else {
            // Particle isn't visible, but it still moves through the field.
            particle.x = xt
            particle.y = yt
          }
        }

        particle.age += 1
      })
    }

    let g = param.canvas.getContext('2d')
    g.lineWidth = PARTICLE_LINE_WIDTH

    const draw = () => {
      g.save()
      g.globalAlpha = 0.16
      g.globalCompositeOperation = 'destination-out'
      g.fillStyle = '#000'
      g.fillRect(bounds.x, bounds.y, bounds.width, bounds.height)
      g.restore()

      buckets.forEach((bucket, i) => {
        if (bucket.length > 0) {
          g.beginPath()
          g.strokeStyle = colorStyles[i]

          bucket.forEach(particle => {
            g.moveTo(particle.x, particle.y)
            g.lineTo(particle.xt, particle.yt)
            particle.x = particle.xt
            particle.y = particle.yt
          })

          g.stroke()
        }
      })
    }

    let then = Date.now()

    const frame = () => {
      animationLoop = requestAnimationFrame(frame)
      let now = Date.now()
      let delta = now - then

      if (delta > FRAME_TIME) {
        then = now - delta % FRAME_TIME
        evolve()
        draw()
      }
    }

    frame()
  }

  const stop = () => {
    if (windy.field) {
      windy.field.release()
    }

    if (animationLoop) {
      cancelAnimationFrame(animationLoop)
    }
  }

  const start = (bounds, width, height, extent) => {
    let mapBounds = {
      south: deg2rad(extent[0][1]),
      north: deg2rad(extent[1][1]),
      east: deg2rad(extent[1][0]),
      west: deg2rad(extent[0][0]),
      width: width,
      height: height
    }

    stop()

    buildGrid(param.data, grid => {
      // interpolateField
      interpolateField(grid, buildBounds(bounds, width, height), mapBounds, (bounds, field) => {
        // animate the canvas with random points
        windy.field = field
        animate(bounds, field, mapBounds)
      })
    })
  }

  const updateData = (data, bounds, width, height, extent) => {
    delete param.data
    param.data = data

    if (extent) {
      start(bounds, width, height, extent)
    }
  }

  const shift = (dx, dy) => {
    let {canvas} = param
    let w = canvas.width
    let h = canvas.height
    let ctx = canvas.getContext('2d')

    if (w > dx && h > dy) {
      const clamp = (high, value) => Math.max(0, Math.min(high, value))

      let imageData = ctx.getImageData(clamp(w, -dx), clamp(h, -dy), clamp(w, w - dx), clamp(h, h - dy))
      ctx.clearRect(0, 0, w, h)
      ctx.putImageData(imageData, clamp(w, dx), clamp(h, dy))

      for (let i = 0, pLength = particles.length; i < pLength; i++) {
        particles[i].x += dx
        particles[i].y += dy
      }
    }
  }

  const windy = {
    param,
    start,
    stop,
    update: updateData,
    shift,
    createField,
    interpolatePoint: interpolate
  }

  return windy
}

// export default class WindFieldUtil {
//   constructor (param) {
//     map.set(this, Object.assign({
//       builder: null,
//       grid: null,
//       date: null,
//       λ0: null,
//       φ0: null,
//       Δλ: null,
//       Δφ: null,
//       ni: null,
//       nj: null
//     }, param))
//   }
//
//   destroy () {
//     map.delete(this)
//   }
//
//   setCanvas (canvas) {
//     let param = map.get(this)
//
//     if (param.canvas === canvas) {
//       return
//     }
//
//     param.canvas = canvas
//   }
//
//   setData (data) {
//     let param = map.get(this)
//
//     if (param.data === data) {
//       return
//     }
//
//     param.data = data
//   }
// }



// WEBPACK FOOTER //
// ./src/components/WindField/WindFieldUtil.js